import { FeatureFlagContext } from '@hopin-team/provider-feature-flag';
import React, { lazy, useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import Loading from './components/loading/loading-component';
import TrackingIntegrations from './components/tracking-integrations/tracking-integrations';
import { authenticatedUserSelector } from './features/authentication/authentication-selectors';
import {
  areDoorsOpen,
  getShowEventSeries,
  selectEventDetails,
} from './features/event-details/event-details-selector';
import { setActiveStep } from './features/manage-steps/manage-steps-reducer';
import { selectActiveStep } from './features/manage-steps/manage-steps-selector';
import { Steps, StepTypes } from './features/manage-steps/manage-steps-types';
import {
  EventRegistrationStatus,
  EventRegistrationStatusTypes,
} from './features/purchase-tickets/purchase-actions';
import { UserBlocked } from './features/user-blocked/user-blocked';
import { useLoadWidget } from './hooks/use-load-widget';
import { useAppDispatch } from './redux/store';
import { FillWindow } from './shared.styles';
import { isQuickRegistrationEligible } from './utils/event';
import {
  FLAG_EVENT_SERIES,
  isQuickRegistrationEnabled,
  isSegmentRegistrationEnabled,
} from './utils/flags';
import { addCustomTiming } from './utils/logging';

const stepMapping: Record<EventRegistrationStatusTypes, StepTypes> = {
  [EventRegistrationStatus.BLOCKED]: Steps.USER_BLOCKED,
  [EventRegistrationStatus.REGISTERED]: Steps.ALREADY_BOUGHT_TICKET,
  [EventRegistrationStatus.NOT_REGISTERED]: Steps.SELECT_TICKETS,
};

const Sessions = lazy(() => import('./features/sessions'));
const RegistrationComplete = lazy(
  () =>
    import('./features/registration-complete/registration-complete-component'),
);
const AssignTickets = lazy(
  () => import('./features/assign-tickets/assign-tickets-component'),
);
const PurchaseTickets = lazy(
  () => import('./features/purchase-tickets/purchase-tickets'),
);
const SelectTickets = lazy(
  () => import('./features/select-tickets/select-tickets-component'),
);
const StepSelection = lazy(() => import('./features/step-selection'));
const SingleRegistrationsStep = lazy(
  () =>
    import('./features/purchase-tickets/single-registration-step-component'),
);
const JoinEvent = lazy(
  () => import('./features/join-event/join-event-component'),
);
const WidgetDisabled = lazy(
  () => import('./components/widget-disabled/widget-disabled-component'),
);
const LoadStripe = lazy(
  () => import('./features/purchase-tickets/stripe/load-stripe'),
);
const SeriesSelectEvents = lazy(
  () =>
    import('./features/series-select-events/series-select-events-component'),
);
const SeriesPayment = lazy(
  () => import('./features/series-payment/series-payment-component'),
);
const SeriesRegistrationComplete = lazy(
  () =>
    import('./features/series-payment/series-registration-complete-component'),
);

const SeriesInformation = lazy(
  () => import('./features/series-information/series-information-component'),
);

export const Registration = () => {
  const { flags } = useContext(FeatureFlagContext);
  const eventDetails = useSelector(selectEventDetails);
  const activeStep = useSelector(selectActiveStep);
  const isEventSeriesFeatureEnabled = useSelector(getShowEventSeries);
  const isEventSeriesFlagEnabled = Boolean(flags[FLAG_EVENT_SERIES]);
  const dispatch = useAppDispatch();
  const { isLoggedIn } = useSelector(authenticatedUserSelector);
  const {
    isLoaded: appIsLoaded,
    eventRegistrationStatus,
    eventLoadingError,
  } = useLoadWidget();
  const timingRef = useRef(false);

  const showQuickRegistration = Boolean(
    isQuickRegistrationEligible(eventDetails, flags) &&
      eventRegistrationStatus === EventRegistrationStatus.NOT_REGISTERED &&
      (!isSegmentRegistrationEnabled(flags) ||
        eventDetails.single_ticket_without_segments),
  );

  const doorsOpen = useSelector(areDoorsOpen);

  useEffect(() => {
    if (!appIsLoaded) return;

    if (!timingRef.current) {
      addCustomTiming('REGISTRATION_SLICE_INTERACTIVE', Date.now());
      timingRef.current = true;
    }

    let step =
      stepMapping[
        eventRegistrationStatus ?? EventRegistrationStatus.NOT_REGISTERED
      ];

    const setQuickRegistrationStep =
      showQuickRegistration && step === Steps.SELECT_TICKETS;

    if (setQuickRegistrationStep) {
      step = Steps.QUICK_REGISTRATION;
    }

    if (isEventSeriesFeatureEnabled && isEventSeriesFlagEnabled) {
      step = Steps.SERIES_SELECT_EVENTS;
    }

    const hasJoinEventAction =
      window.location.search.includes('action=join_event');

    if (
      hasJoinEventAction &&
      isQuickRegistrationEnabled(flags) &&
      doorsOpen &&
      !isLoggedIn
    ) {
      step = Steps.JOIN_EVENT;
    }

    dispatch(setActiveStep(step));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appIsLoaded,
    dispatch,
    doorsOpen,
    eventRegistrationStatus,
    isLoggedIn,
    showQuickRegistration,
  ]);

  return (
    <>
      <TrackingIntegrations />
      <FillWindow
        as="main"
        data-testid="registration-container"
        className="registration-slice-container"
      >
        <RenderStep
          step={activeStep}
          disabled={appIsLoaded && eventLoadingError}
        />
      </FillWindow>
    </>
  );
};

const RenderStep = ({
  step,
  disabled,
}: {
  step: string;
  disabled: boolean;
}) => {
  if (disabled) {
    return <WidgetDisabled />;
  }

  switch (step) {
    case Steps.JOIN_EVENT:
      return <JoinEvent />;
    case Steps.QUICK_REGISTRATION:
      return (
        <LoadStripe>
          <SingleRegistrationsStep />
        </LoadStripe>
      );
    case Steps.USER_BLOCKED:
      return <UserBlocked />;
    case Steps.ALREADY_BOUGHT_TICKET:
      return <StepSelection />;
    case Steps.SERIES_SELECT_EVENTS:
      return <SeriesSelectEvents />;
    case Steps.SELECT_TICKETS:
      return <SelectTickets />;
    case Steps.PURCHASE_TICKETS:
      return (
        <LoadStripe>
          <PurchaseTickets />
        </LoadStripe>
      );
    case Steps.ASSIGN_TICKETS:
      return <AssignTickets />;
    case Steps.REGISTRATION_COMPLETE:
      return <RegistrationComplete />;
    case Steps.SESSIONS:
      return <Sessions />;
    case Steps.INFORMATION:
      return <SeriesInformation />;
    case Steps.CHECKOUT:
      return (
        <LoadStripe>
          <SeriesPayment />
        </LoadStripe>
      );
    case Steps.SERIES_REGISTRATION_COMPLETE:
      return <SeriesRegistrationComplete />;
    case Steps.LOADING:
    default:
      return <Loading />;
  }
};
